import type { BuildStatus } from '@hangar/shared';
import type { Build } from '@/api/schemas';
import { elapsedSeconds } from '@/lib/format';
import { STATUS_LABEL, statusLabel } from './buildLabels';

export { STATUS_LABEL, STATUS_LABEL_SHORT } from './buildLabels';

export const ALL_STATUSES = Object.keys(STATUS_LABEL) as BuildStatus[];
export const TERMINAL = new Set<BuildStatus>(['succeeded', 'failed', 'errored', 'canceled', 'timed_out', 'expired']);
export const isTerminal = (s: BuildStatus) => TERMINAL.has(s);
export const canCancel = (b: Build) => !isTerminal(b.status) && !b.cancelRequestedAt;
/** `POST /retry` only accepts errored / timed_out (api/openapi.yaml). */
export const canRetry = (b: Build) => b.status === 'errored' || b.status === 'timed_out';
/** `POST /priority` only while waiting (pending_approval, queued). */
export const canReprioritize = (b: Build) => b.status === 'pending_approval' || b.status === 'queued';

/** Live or final duration in seconds: startedAt → finishedAt (or now while running). */
export function buildDuration(b: Build, now: number): number | null {
  if (b.durationSeconds !== null && b.durationSeconds !== undefined) return b.durationSeconds;
  if (!b.startedAt) return null;
  return elapsedSeconds(b.startedAt, b.finishedAt ?? null, now);
}

export interface Step {
  key: string;
  label: string;
  detail?: string;
  state: 'done' | 'active' | 'todo' | 'failed' | 'skipped';
}

const ORDER: BuildStatus[] = ['pending_approval', 'awaiting_client', 'queued', 'dispatching', 'provider_queued', 'running', 'verifying', 'succeeded'];
/** Rank of the latest of these statuses in the lifecycle order. */
const stage = (statuses: BuildStatus[]) => Math.max(...statuses.map((s) => ORDER.indexOf(s)));

/** `label` names a step not reached yet (and a failed one); `doneLabel` a step behind the build. */
type StepDef = { key: string; label: string; doneLabel?: string; from: BuildStatus[]; detail?: string };

/** Where a failed/canceled build stopped: the last non-terminal stage it reached. */
export function stoppedStage(b: Build): number {
  if (b.finishedAt && b.startedAt) return stage([b.error?.category === 'policy' ? 'verifying' : 'running']);
  if (b.dispatchedAt) return stage(['provider_queued']);
  return stage([b.queuedAt ? 'queued' : 'pending_approval']);
}

/** Who asked for the build, as the first step's detail ("Console", "bitbucket #12", the source). */
const requestedDetail = (b: Build): string => (b.source === 'ui' ? 'Console' : b.ciContext?.provider ? `${b.ciContext.provider} #${b.ciContext.buildNumber ?? ''}` : b.source);

/** The steps in neutral words (SPEC decision 23): the build service is never named, nor its account. */
const stepDefs = (b: Build, failedAt: boolean): StepDef[] => {
  return [
    { key: 'requested', label: 'Requested', from: ['pending_approval', 'awaiting_client'], detail: requestedDetail(b) },
    { key: 'queued', label: 'Queued', from: ['queued'], detail: b.statusReason && b.status === 'queued' ? b.statusReason : undefined },
    { key: 'dispatched', label: 'Send to the build service', doneLabel: 'Sent to the build service', from: ['dispatching', 'provider_queued'] },
    { key: 'building', label: 'Build', doneLabel: 'Built', from: ['running'] },
    { key: 'verifying', label: 'Checks', doneLabel: 'Checked', from: ['verifying'] },
    { key: 'done', label: b.status === 'succeeded' ? 'Succeeded' : failedAt ? STATUS_LABEL[b.status] : 'Done', from: ['succeeded'] },
  ];
};

/**
 * One step's state: a failed build is done up to where it stopped, failed there, skipped after (the last step fails too).
 * A live build is active on the step that holds its status (`from`), done before it, todo after.
 */
export function stepState(key: string, r: number, b: Build, from: readonly BuildStatus[] = []): Step['state'] {
  const rank = ORDER.indexOf(b.status);
  if (isTerminal(b.status) && b.status !== 'succeeded') {
    const stopped = stoppedStage(b);
    if (r < stopped) return 'done';
    return r === stopped || key === 'done' ? 'failed' : 'skipped';
  }
  if (from.includes(b.status) || r === rank) return b.status === 'succeeded' ? 'done' : 'active';
  return r < rank ? 'done' : 'todo';
}

/** The label a step shows in its state: the active step says what the status says ("Waiting in the build queue"), so they agree. */
function stepLabel(s: StepDef, state: Step['state'], b: Build): string {
  if (state === 'active') return statusLabel(b.status);
  return state === 'done' && s.doneLabel ? s.doneLabel : s.label;
}

/** Lifecycle stepper of screen 03 (Requested → Queued → Send → Build → Checks → Done) from the status. */
export function lifecycleSteps(b: Build): Step[] {
  const failedAt = isTerminal(b.status) && b.status !== 'succeeded';
  return stepDefs(b, failedAt).map((s) => {
    const state = stepState(s.key, stage(s.from), b, s.from);
    return { key: s.key, label: stepLabel(s, state, b), detail: s.detail, state };
  });
}

/** The step the build is on (active, else where it failed, else the last one done) and its 1-based position. */
export function currentStep(steps: Step[]): { step: Step; position: number } | undefined {
  const i = steps.findIndex((s) => s.state === 'active' || s.state === 'failed');
  const at = i >= 0 ? i : steps.map((s) => s.state).lastIndexOf('done');
  return at >= 0 ? { step: steps[at]!, position: at + 1 } : undefined;
}

/** How far along the stepper is, 0–100: done steps count whole, the active one half. */
export function stepProgress(steps: Step[]): number {
  if (steps.length === 0) return 0;
  const done = steps.filter((s) => s.state === 'done').length + steps.filter((s) => s.state === 'active').length / 2;
  return Math.round((done / steps.length) * 100);
}

/** Requester chip copy: "bitbucket · storeai-frontend #1287" / "jose.zamora@…" / "schedule". */
export function requesterOf(b: Build): { icon: string; label: string; sub?: string } {
  if (b.ciContext?.provider) return { icon: 'account_tree', label: `${b.ciContext.repository ?? b.ciContext.provider}${b.ciContext.buildNumber ? ` #${b.ciContext.buildNumber}` : ''}`, sub: b.ciContext.provider };
  if (b.actor.type === 'user') return { icon: 'person', label: b.actor.label, sub: b.source };
  if (b.source === 'schedule') return { icon: 'schedule', label: 'Schedule', sub: b.actor.label };
  if (b.source === 'redeploy') return { icon: 'refresh', label: 'Redeploy', sub: b.actor.label };
  return { icon: 'terminal', label: b.actor.label, sub: b.source };
}

export const PLATFORM_LABEL = { android: 'Android', ios: 'iOS' } as const;

/**
 * Whether the console may offer Run build for a profile: Hangar must start the build itself — an EAS profile that allows
 * the runner (`hangar_runner`, when the API reports it: `features.runner`), whatever else it allows, because the console
 * asks for that mode explicitly; or a Bitrise `provider_api` profile that does not also build from CI. A CI-only profile
 * (`ci_client`) never: such a build waits for a runtime token a console session never receives and holds a provider slot
 * until it expires. The launch form's `startable` stays the authority (the Run build sheet blocks on it, for instance
 * when the app is not linked to its repository yet).
 */
export const startsFromConsole = (profile: { executionModes: readonly string[] }, runner?: boolean): boolean =>
  (runner === true && profile.executionModes.includes('hangar_runner')) || (!profile.executionModes.includes('ci_client') && profile.executionModes.includes('provider_api'));

export const CI_ONLY_REASON = 'This profile builds from your CI with hangar build';
